import { Injectable, Signal, WritableSignal, computed, effect, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, UrlTree } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';

export type HeaderRouteData = {
  route: string;
  title: string;
  backButton?: boolean;
  backRoute?: string | Array<string>;
  children?: HeaderChildRouteData[];
};

export type HeaderChildRouteData = {
  route: string;
  title: string;
  backButton?: boolean;
  backRoute?: string | Array<string>;
  params?: any;
};

@Injectable()
export class HeaderService {
  routes: WritableSignal<HeaderRouteData[]> = signal([]);

  private navigationEvent: Signal<any>;

  public headerDetails: Signal<HeaderRouteData | HeaderChildRouteData>;

  constructor(private router: Router) {
    this.navigationEvent = toSignal(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd)
      ),
      {}
    );

    this.headerDetails = computed(() => {
      return this.navigationEvent() ? this.isOnRouteOrChild(this.routes()) : null;
    });

  }

  replaceToken(token, value) {
    const routes = this.routes();
  
    const updatedRoutes = routes.map(route => {
      if (Array.isArray(route.backRoute)) {
        route.backRoute = route.backRoute.map(backRouteItem => {
          if (backRouteItem.includes(token)) {
            return backRouteItem.replace(token, value);
          }
          return backRouteItem;
        });
      } else {
        if (route.backRoute.includes(token)) {
          route.backRoute = route.backRoute.replace(token, value);
        }
      }
      return route;
    });
  
    this.routes.set(updatedRoutes);
  }

  isOnRouteOrChild(
    routes: HeaderRouteData[]
  ): HeaderRouteData | HeaderChildRouteData {
    const currentUrlTree: UrlTree = this.router.parseUrl(this.router.url);
    const currentRoute: string[] = currentUrlTree.root.children[
      'primary'
    ].segments.map((segment) => segment.path);

    for (let routeDefinition of routes) {
      if (currentRoute.includes(routeDefinition.route)) {
        if (routeDefinition.children) {
          let childWithParams = this.GetDynamicChild(
            currentRoute,
            routeDefinition
          );

          if (childWithParams) {
            return childWithParams;
          }

          let childRoute = routeDefinition.children.find(
            (childRoute) =>
              !childRoute.route.startsWith(':') &&
              currentRoute.includes(childRoute.route)
          );

          if (childRoute) {
            return childRoute;
          }
        }
        return routeDefinition;
      } else if (routeDefinition.route.includes(':id')) {
        let selector = routeDefinition.route.split('/')[0];

        const isBaseRoute = new RegExp(
          `.*\\/${selector}\\/([a-zA-Z0-9:-]+)(?![\\/])$`
        );
        const isChildRoute = new RegExp(
          `.*\\/${selector}\\/([a-zA-Z0-9:-]+).*\\/`
        );
        const backRouteRegex = new RegExp(
          `(?![\\/])${selector}\\/([a-zA-Z0-9:-]+)`
        );

        if (isBaseRoute.test(currentRoute.join('/'))) {
          return routeDefinition;
        } else if (isChildRoute.test(currentRoute.join('/'))) {
          if (routeDefinition.children) {
            let childWithParams = this.GetDynamicChild(
              currentRoute,
              routeDefinition
            );

            if (childWithParams) {
              return childWithParams;
            }

            let childRoute = routeDefinition.children.find(
              (childRoute) =>
                !childRoute.route.startsWith(':') &&
                currentRoute.includes(childRoute.route)
            );

            if (childRoute) {
              childRoute.backRoute = currentRoute
                .join('/')
                .match(backRouteRegex)[0]
                .split('/');
              return childRoute;
            }
          }
        }
      }
    }
    return null;
  }

  navigateToParent(parentRoute) {
    if (parentRoute !== null) {
      this.router.navigate([...parentRoute]);
    }
  }

  private GetDynamicChild(currentRoute, routeDefinition: HeaderRouteData) {
    const dynamicChildRoute = routeDefinition.children.find((childRoute) =>
      childRoute.route.startsWith(':')
    );

    if (
      dynamicChildRoute &&
      currentRoute.length > currentRoute.indexOf(routeDefinition.route) + 1 &&
      currentRoute[currentRoute.length - 1] !== ''
    ) {
      dynamicChildRoute.params = currentRoute[currentRoute.length - 1];
      return dynamicChildRoute;
    } else {
      return null;
    }
  }
}
